import React, { useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { CSSTransition, TransitionGroup } from 'react-transition-group'

import { BlockLevelInterface } from '@store/actionSlices/blockLevel'
import { RouteKey } from '@store/constants'
import {
  PlayerState,
  type ProjectIdentity,
  type RootStateTypeExtra,
} from '@store/types'

import { ComparePropertyToggleAction } from '@components/compare-property'
import usePropertyCompareFirebaseTrigger from '@components/compare-property/utils'
import Container from '@components/container'
import DataHandler from '@components/data-handler'
import { Status } from '@components/data-handler/data-handler'
import Filter from '@components/filter'
import FloorPlateGallery from '@components/floor-plate-gallery'
import { InfoModal } from '@components/modals'
import { MarkerType } from '@components/showcase-canvas/constants'
import { PannellumDataInterface as PanoramicDataInterface } from '@components/showcase-pannellum/types'
import SlideshowControl from '@components/slideshow-control'
import UnitListing from '@components/unit-listing'
import ZoneDropdown from '@components/zone-dropdown'

import { Level, selectFromResult, useGetBuildingQuery } from '@api/building'
import {
  selectFromResult as selectFromExternalLinkResult,
  useGetExternalLinksByPropertyQuery,
} from '@api/external-links'
import {
  FloorPlateGalleryInterface,
  FloorPlateGalleryItemInterface,
  selectFromResult as selectFromFloorGalleryResult,
  useGetFloorPlateGalleryQuery,
} from '@api/floor-plate-gallery'
import {
  MapContent,
  Polygon,
  selectFromResult as selectInteractiveFromResult,
  useGetInteractivePlanQuery,
} from '@api/interactive-plan'
import {
  selectFromResult as selectFromPanoramicResult,
  useGetPanoramicQuery,
} from '@api/panoramic'

import useCheckMountedStatus from '@utilities/check-mounted-status'
import { getTabIndexByKey, getTabKeyByIndexBy } from '@utilities/floor-tab'
import mapWhiteLabelsWithDefaults from '@utilities/white-label'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import { SessionMap } from '@firebaseUtil/types'

import { ChevronSvg, FunnelSvg, PanoramaSvg } from '@svg/react'

import {
  useCompareProperty,
  useSyncComparablePropertiesFromSession,
} from '@adUtilities/components/compare-property/utils'
import { PropertyType, RequiredComparableProperty } from '@adUtilities/types'
import { Unit } from '@adUtilities/types/apartment'

import GeneralExternalLinkButton from '../common/GeneralExternalLinkButton'
import useGetGeneralExternalLinks from '../hooks/useGetGeneralExternalLinks'
import BuildingSkeleton from './building-skeleton'
import FloorGalleryTab from './floor-gallery-tab'
import UnitDetailsView from './unit-details-view'
import UnitGallery from './unit-gallery'

export interface BuildingProps {
  session: SessionMap | undefined
  projectIdentity: ProjectIdentity
  blockLevel: BlockLevelInterface
  buildingBackButtonTitle: string
  floorViewPanoramicButtonTitle: string
  propertyViewPanoramicButtonTitle: string
  externalLinkModalTitle: string
  externalLinkModalBody: string
}

function Building({
  projectIdentity,
  session,
  blockLevel,
  buildingBackButtonTitle,
  floorViewPanoramicButtonTitle,
  propertyViewPanoramicButtonTitle,
  externalLinkModalTitle,
  externalLinkModalBody,
}: BuildingProps) {
  const history = useHistory()
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })
  const [activeBlockTitle, setActiveBlockTitle] = React.useState('')
  const [floorGalleryActiveTab, setFloorGalleryActiveTab] =
    React.useState('floor')
  const [activeBlock, setActiveBlock] = React.useState('')
  const [activeLevel, setActiveLevel] = React.useState('')
  const [activeUnit, setActiveUnit] = React.useState('')
  const [unitSelected, setUnitSelected] = React.useState<Unit>()
  const [isFilterOpen, toggleFilter] = React.useState(false)
  const [units, setUnits] = React.useState<Array<Unit> | null>(null)
  const [unitGalleryItemLength, setUnitGalleryItemLength] = React.useState(0)
  const [shouldShowSlideshowBtn, setShouldShowSlideShowBtn] =
    React.useState(false)
  const [disableSlideShowButton, setDisableSlideShowButton] =
    React.useState<boolean>(false)
  const [toggleExternalLinkPopup, setToggleExternalLinkPopup] =
    React.useState(false)

  const buildingPayload = useGetBuildingQuery(
    { projectName: projectIdentity.projectId },
    { selectFromResult }
  )

  const floorGalleryPayload = useGetFloorPlateGalleryQuery(
    { projectName: projectIdentity.projectId },
    { selectFromResult: selectFromFloorGalleryResult }
  )

  const interactivePayload = useGetInteractivePlanQuery(
    { projectName: projectIdentity.projectId },
    { selectFromResult: selectInteractiveFromResult }
  )

  const { floorPlateGalleryData } = floorGalleryPayload

  const { panoramicData } = useGetPanoramicQuery(
    { projectName: projectIdentity.projectId },
    { selectFromResult: selectFromPanoramicResult }
  )

  const { levels, isLoaded } = buildingPayload

  const externalLinksPayload = useGetExternalLinksByPropertyQuery(
    { projectName: projectIdentity.projectId, propertyId: activeUnit },
    {
      skip: !activeUnit,
      selectFromResult: selectFromExternalLinkResult,
    }
  )

  const { externalLinks } = externalLinksPayload

  const { generalExternalLink } = useGetGeneralExternalLinks()

  const handleUnits = () => {
    setUnits(
      levels
        .find((lvl: Level) => lvl.level === activeLevel)
        ?.data.filter((unit: Unit) => {
          if (activeBlock) {
            return unit.blockId === activeBlock
          }
          return unit
        }) || []
    )
  }

  const handleInfoPopup = async () => {
    await firebaseControlQuery.update({
      [`building.unitInfoPopup`]: !session?.building.unitInfoPopup,
    })
  }

  const handleExternalLinkPopup = async () => {
    await firebaseControlQuery.update({
      [`propertyGallery.galleryControlV2.autoPlayState`]: false,
      [`building.externalLinkPopup`]: !toggleExternalLinkPopup,
    })
  }

  const handleUnitClick = (unit: Unit) => {
    toggleFilter(false)
    firebaseControlQuery.update({ [`building.activeUnit`]: unit.name })
  }

  const removeUnit = async (newPathname?: string) => {
    let removeUnitPayload: Record<string, unknown> = {
      [`building.unitInfoPopup`]: false,
      [`propertyGallery.galleryControlV2.activeSlideIndex`]: 0,
      [`propertyGallery.galleryControlV2.autoPlayState`]: false,
      [`propertyGallery.playerControlV2.playerState`]: PlayerState.Stop,
    }

    if (newPathname !== '/panoramic') {
      removeUnitPayload = {
        ...removeUnitPayload,
        [`building.activeUnit`]: '',
      }
    }

    await firebaseControlQuery.update(removeUnitPayload)
  }

  const handleBackButtonPress = async () => {
    let route = RouteKey.LEVEL_VIEW

    const { isSnaploaderActive, isEnvisionVRActive, showcase3DIntegration } =
      projectIdentity

    if (isSnaploaderActive) {
      route = RouteKey.SNAPLOADER_VIEW
    } else if (isEnvisionVRActive) {
      route = RouteKey.THREE_D_BUILDING
    } else if (showcase3DIntegration) {
      route = RouteKey.ADTHREED
    }

    await firebaseControlQuery.update({
      [`activeRoute`]: route,
    })

    await firebaseControlQuery.update({
      [`building.activeLevel`]: '',
      [`floorGallery.galleryControlV2.activeItemIndex`]: 0,
      [`floorGallery.galleryControlV2.activeTabIndex`]: 0,
    })
  }

  const getTargetBlock = (
    blocks: Record<string, Array<MapContent>>,
    activeBlock?: string
  ): Array<MapContent> | undefined => {
    if (activeBlock) {
      return blocks[activeBlock]
    }

    if (Object.keys(blocks).length === 1) {
      return Object.values(blocks)?.[0]
    }

    return undefined
  }

  const findPolygonByGroupId = (
    block: Array<MapContent>,
    groupId: string
  ): Polygon | undefined =>
    block
      .flatMap((mapContent) => mapContent.polygons)
      .find((poly) => poly.groupId === groupId)

  const isLabelOnlyType = (type?: string): boolean =>
    [
      MarkerType.arrow,
      MarkerType.arrowSmall,
      MarkerType.oblongLabel,
      MarkerType.oblongStatus,
    ].includes(type || '')

  const formatPolygonTitle = (polygon: Polygon): string =>
    isLabelOnlyType(polygon.type)
      ? (polygon.label ?? polygon.groupId)
      : `${polygon.label ?? ''} ${polygon.groupId}`.trim()

  const getLevelTitle = useCallback(
    (activeLevel: string, activeBlock?: string): string => {
      const { blocks } = blockLevel

      if (!blocks) {
        return activeLevel
      }

      const targetBlock = getTargetBlock(blocks, activeBlock)

      if (!targetBlock) {
        return activeLevel
      }

      const matchedPoly = findPolygonByGroupId(targetBlock, activeLevel)

      if (!matchedPoly) {
        return activeLevel
      }

      return formatPolygonTitle(matchedPoly)
    },
    [blockLevel]
  )

  const buildingPageTitle = useMemo(() => {
    if (activeUnit) {
      return activeUnit
    }

    return getLevelTitle(activeLevel, activeBlock)
  }, [activeUnit, activeLevel, activeBlock, getLevelTitle])

  const findPanoramic = (
    type: 'unit' | 'level' | 'block',
    targetName: string
  ) =>
    panoramicData.find(
      (data: PanoramicDataInterface) =>
        data.type === type &&
        (data.panoramaGroup || '')?.toString() === targetName
    )

  const hasPanoramicPayload = () => {
    if (activeUnit !== '') {
      return findPanoramic('unit', activeUnit?.toString() || '') !== undefined
    }

    if (activeLevel !== '') {
      const activePanoramaKey =
        Object.keys(blockLevel?.blocks || {}).length > 1
          ? `${activeBlock}-${activeLevel}`
          : activeLevel
      return findPanoramic('level', activePanoramaKey || '') !== undefined
    }

    if (activeBlock !== '') {
      return findPanoramic('block', activeBlock?.toString() || '') !== undefined
    }

    return false
  }

  const handleFloorGalleryTabClick = async (tabKey: string) => {
    await firebaseControlQuery.update({
      [`floorGallery.galleryControlV2.activeSlideIndex`]: 0,
      [`floorGallery.galleryControlV2.activeTabIndex`]:
        getTabIndexByKey(tabKey),
    })
  }

  const floorGalleryItems =
    React.useMemo((): Array<FloorPlateGalleryItemInterface> => {
      let foundData = null

      if (activeBlock && activeLevel) {
        foundData = floorPlateGalleryData.find(
          (value: FloorPlateGalleryInterface) =>
            value.block.toString() === activeBlock.toString() &&
            value.level.toString() === activeLevel.toString()
        )
      } else if (activeLevel) {
        foundData = floorPlateGalleryData.find(
          (value: FloorPlateGalleryInterface) =>
            value.level.toString() === activeLevel.toString()
        )
      }

      return foundData?.assets || []
    }, [floorPlateGalleryData, activeBlock, activeLevel])

  const handleGoToPanoramic = async () => {
    await firebaseControlQuery.update({
      [`panoramic.scene`]: '',
    })
    firebaseControlQuery.updateRoute('panoramic')
    history.push('panoramic')
  }

  const blockOptions = React.useMemo(() => {
    const { blocks } = blockLevel
    if (blocks) {
      return Object.keys(blocks)?.map((item) => ({
        label: item,
        value: item,
      }))
    }
    return []
  }, [blockLevel.blocks])
  const getFirstLevel = React.useCallback(
    (selectedBlock: string) => {
      const levelList: Level[] = [...blockLevel.levels]
      const filteredLevels = levelList.filter((lvl) =>
        session?.building.unitFilter.showAvailable
          ? lvl.data.some(
              (unit: Unit) =>
                unit.metas.status === projectIdentity.statusLabels?.available
            )
          : true
      )
      const firstLevel = filteredLevels.find((lvl) =>
        lvl.data.some((unit) => unit.blockId === selectedBlock)
      )
      return firstLevel?.level || filteredLevels[0]?.level
    },
    [
      blockLevel.levels,
      projectIdentity.statusLabels,
      session?.building.unitFilter.showAvailable,
    ]
  )
  const onChangeBlock = React.useCallback(
    async (item: string) => {
      const firstLevel = getFirstLevel(item)
      await firebaseControlQuery.update({
        [`building.activeBlock`]: item,
        [`building.activeLevel`]: firstLevel,
        [`building.activeUnit`]: '',
      })
    },
    [firebaseControlQuery]
  )

  React.useEffect(() => {
    if (Object.keys(levels).length > 0) {
      handleUnits()
    }
  }, [activeBlock, activeLevel, isLoaded, levels])

  React.useEffect(() => {
    const unit = units?.find((res) => res.name === activeUnit)
    setUnitSelected(unit)
  }, [activeUnit, units])

  React.useEffect(() => {
    if (session) {
      const {
        building: {
          activeBlock: firebaseActiveBlock,
          activeLevel: firebaseActiveLevel,
          activeUnit: firebaseActiveUnit,
          externalLinkPopup: externalLinkPopupFirebase,
        },
        floorGallery: {
          galleryControlV2: {
            activeTabIndex: firebaseActiveFloorGalleryTabIndex,
          },
        },
        propertyGallery: {
          galleryControlV2: { autoPlayState: firebaseAutoPlayState },
          playerControlV2: { playerState: firebasePlayerState },
        },
      } = session

      if (
        firebaseActiveUnit === '' &&
        (firebaseAutoPlayState || firebasePlayerState === 'play')
      ) {
        firebaseControlQuery.update({
          [`propertyGallery.galleryControlV2.activeSlideIndex`]: 0,
          [`propertyGallery.galleryControlV2.autoPlayState`]: false,
          [`propertyGallery.playerControlV2.playerState`]: PlayerState.Stop,
        })
      }

      setActiveBlock(firebaseActiveBlock)
      setActiveLevel(firebaseActiveLevel)
      setActiveUnit(firebaseActiveUnit)
      setToggleExternalLinkPopup(externalLinkPopupFirebase)

      setFloorGalleryActiveTab(
        getTabKeyByIndexBy(firebaseActiveFloorGalleryTabIndex)
      )
      setDisableSlideShowButton(firebasePlayerState === 'play')
    }
  }, [session])

  React.useEffect(() => {
    setShouldShowSlideShowBtn(unitGalleryItemLength > 1)
  }, [unitGalleryItemLength])

  React.useEffect(() => {
    const removeUnitOnRouteChange = (location: Location) => {
      const { pathname } = location
      firebaseControlQuery.update({
        [`building.externalLinkPopup`]: false,
        [`building.generalExternalLinkPopup`]: false,
      })
      removeUnit(pathname).catch((error) => console.error(error))
    }

    const routeChangeUnsubscribe = history.listen(removeUnitOnRouteChange)

    return () => {
      routeChangeUnsubscribe()
    }
  }, [history])

  const dataHandlerPayload = React.useMemo(() => {
    if (externalLinksPayload.status === Status.UNINITIALIZED) {
      return buildingPayload
    }

    if (externalLinksPayload.status !== Status.FULFILLED) {
      return externalLinksPayload
    }

    return buildingPayload
  }, [externalLinksPayload, buildingPayload])

  const isSlideShowPlaying =
    !!session?.propertyGallery?.galleryControlV2?.autoPlayState
  const hideComparePropertyAction = React.useMemo(() => {
    if (session?.propertyGallery?.galleryControlV2?.activeSlideIndex !== 0) {
      return true
    }
    return false
  }, [session?.propertyGallery?.galleryControlV2?.activeSlideIndex])

  const comparePropertyProps = useCompareProperty()
  const { compareModeIsActive } = comparePropertyProps

  const { triggerComparisonStop } = usePropertyCompareFirebaseTrigger({
    comparePropertyProps,
  })

  useSyncComparablePropertiesFromSession({
    disable: !session?.connected,
    comparePropertyProps,
    data: buildingPayload.levels,
    dataLoaded: buildingPayload.status === Status.FULFILLED,
    firebasePropertyCompare: session?.propertyCompare,
    propertyType: PropertyType.Unit,
  })

  const isMounted = useCheckMountedStatus({
    disable: !session?.connected,
  })

  React.useEffect(() => {
    if (!isMounted) {
      return
    }
    triggerComparisonStop()
  }, [
    session?.building?.activeBlock,
    session?.building?.activeLevel,
    session?.building?.activeUnit,
  ])

  const comparePropertyToggleActionProperty = React.useMemo<
    RequiredComparableProperty['property'] | undefined
  >(
    () =>
      unitSelected
        ? {
            propertyType: PropertyType.Unit,
            data: unitSelected,
            references: {
              levelId: unitSelected.metas.level,
            },
          }
        : undefined,
    [unitSelected]
  )

  React.useEffect(() => {
    const { maps } = interactivePayload
    const { blocks } = blockLevel

    if (Object.keys(blocks || {}).length === 1) {
      const block = maps?.areaView?.polygons[0]
      const blockTitle =
        Object.keys(blocks)?.[0] || block?.label || block?.groupId || ''
      setActiveBlockTitle(blockTitle)
    }
  }, [blockLevel, interactivePayload.isLoaded])

  return (
    <Container>
      <DataHandler
        message="There are no available units."
        payloads={{
          ...dataHandlerPayload,
          data: buildingPayload.levels,
        }}
        skeletonFrame={<BuildingSkeleton />}
      >
        {session && (
          <Filter
            toggle={toggleFilter}
            isOpen={isFilterOpen}
            firebaseUnitFilter={session.building.unitFilter}
            firebaseActiveBlock={session.building.activeBlock}
            firebaseActiveLevel={session.building.activeLevel}
          />
        )}
        <div className="h-full w-full px-4">
          <div
            className={`w-full py-3 text-neutralColour ${compareModeIsActive ? '' : 'h-top-bar'}`}
          >
            <div className="flex h-[40%] items-start justify-between text-default font-normal">
              <div className="relative">
                <button
                  onClick={() => {
                    if (activeUnit) {
                      removeUnit()
                      return
                    }
                    handleBackButtonPress()
                  }}
                  type="button"
                  className="relative inline-flex items-center"
                >
                  <ChevronSvg className="absolute -left-2.5 h-8 w-8" />

                  <span className="ml-6">
                    {activeUnit
                      ? `${buildingBackButtonTitle} ${activeLevel}`
                      : session?.building?.activeBlock || activeBlockTitle}
                  </span>
                </button>

                {!activeUnit && blockOptions.length > 1 && (
                  <div className="absolute -right-12 top-0.5">
                    <ZoneDropdown
                      activeZone={session?.building.activeBlock || ''}
                      zoneOptions={blockOptions}
                      onChange={onChangeBlock}
                      setActiveZone={setActiveBlock}
                    />
                  </div>
                )}
              </div>

              <div className="flex items-start gap-5">
                {!activeUnit &&
                  !compareModeIsActive &&
                  (floorGalleryItems.length > 0 ||
                    hasPanoramicPayload() ||
                    generalExternalLink) && (
                    <div className="flex items-center ipad-mini:text-sm ipad-mini-land:text-lg [@media_(min-width:1280px)]:text-[25px] [@media_(min-width:1280px)]:leading-[37.5px]">
                      {floorGalleryItems.length > 0 && (
                        <FloorGalleryTab
                          activeTab={floorGalleryActiveTab}
                          handleClick={handleFloorGalleryTabClick}
                        />
                      )}
                      {hasPanoramicPayload() && (
                        <button
                          onClick={handleGoToPanoramic}
                          type="button"
                          className={`ml-2 truncate rounded-lg border border-mainColour bg-white px-3 py-2 tracking-widest text-mainColour ipad-mini-land:max-w-80 ${generalExternalLink ? 'ipad-mini:max-w-24' : 'ipad-mini:max-w-48'}`}
                        >
                          {floorViewPanoramicButtonTitle}
                        </button>
                      )}

                      <GeneralExternalLinkButton
                        hasPanoramicButton={hasPanoramicPayload()}
                      />
                    </div>
                  )}

                {activeUnit &&
                  hasPanoramicPayload() &&
                  !compareModeIsActive && (
                    <button
                      onClick={handleGoToPanoramic}
                      type="button"
                      className="inline-flex items-center"
                    >
                      <span className="mr-1">
                        {propertyViewPanoramicButtonTitle}
                      </span>
                      <PanoramaSvg className="h-8 w-8" />
                    </button>
                  )}

                {!isSlideShowPlaying && comparePropertyToggleActionProperty ? (
                  <ComparePropertyToggleAction
                    comparePropertyProps={comparePropertyProps}
                    property={comparePropertyToggleActionProperty}
                    hide={hideComparePropertyAction}
                  />
                ) : null}
              </div>
            </div>
            {!compareModeIsActive && (
              <div className="flex h-[60%] items-baseline justify-between">
                <div className="text-title font-medium">
                  {buildingPageTitle}
                </div>
                {activeUnit && shouldShowSlideshowBtn && (
                  <SlideshowControl
                    galleryName="propertyGallery"
                    isDisabled={disableSlideShowButton}
                  />
                )}
                {!activeUnit &&
                  !projectIdentity.hideFilter &&
                  floorGalleryActiveTab === 'floor' && (
                    <button
                      type="button"
                      onClick={() => toggleFilter(true)}
                      className="inline-flex items-center text-default"
                    >
                      <span className="mr-1">Filters</span>
                      <FunnelSvg className="h-6 w-6" />
                    </button>
                  )}
              </div>
            )}
          </div>

          <div
            className={`no-scrollbar relative w-full overflow-auto ${comparePropertyProps.compareModeIsActive ? 'h-unit-property-comparison-body max-h-[890px]' : 'h-page-body'}`}
          >
            <TransitionGroup>
              <CSSTransition
                key={unitSelected?.id || floorGalleryActiveTab}
                classNames={
                  activeUnit || floorGalleryActiveTab === 'gallery'
                    ? 'transition-slide-left'
                    : 'transition-slide-right'
                }
                timeout={300}
                exit={false}
                onEntered={(node: HTMLElement) =>
                  node.classList.remove('transition-slide-left-enter-done')
                }
              >
                <div className="absolute h-full w-full">
                  {unitSelected && (
                    <UnitDetailsView
                      unit={unitSelected}
                      handleInfoPopup={handleInfoPopup}
                      unitInfoPopup={Boolean(session?.building.unitInfoPopup)}
                      showPrice={projectIdentity.showPrice}
                      hideSoldPrice={projectIdentity.hideSoldPrice}
                      handleExternalLinkPopup={handleExternalLinkPopup}
                      showExternalLink={externalLinks?.length > 0}
                      availableStatusLabel={
                        projectIdentity?.statusLabels?.available
                      }
                      comparePropertyProps={comparePropertyProps}
                      toggleExternalLinkPopup={toggleExternalLinkPopup}
                      isSlideShowPlaying={isSlideShowPlaying}
                    >
                      <UnitGallery
                        hasBlurredEffect
                        imgClass="object-cover !h-auto"
                        key={unitSelected.id}
                        unit={unitSelected}
                        getUnitGalleryItemLength={setUnitGalleryItemLength}
                        onlyShowUnitGalleries={
                          projectIdentity?.onlyShowPropertyGalleries
                        }
                      />
                    </UnitDetailsView>
                  )}

                  {!unitSelected &&
                    !!units &&
                    session &&
                    floorGalleryActiveTab === 'floor' &&
                    session.activeRoute === 'building' && (
                      <UnitListing
                        units={units}
                        trigger={handleUnitClick}
                        filter={session.building.unitFilter}
                        disableSoldUnit={projectIdentity?.disableSoldProperty}
                      />
                    )}
                  {!unitSelected &&
                    floorGalleryActiveTab === 'gallery' &&
                    floorGalleryItems.length > 0 && (
                      <FloorPlateGallery
                        floorGalleryItems={floorGalleryItems}
                      />
                    )}
                </div>
              </CSSTransition>
            </TransitionGroup>
          </div>
        </div>
      </DataHandler>

      {toggleExternalLinkPopup && (
        <InfoModal
          isVisible={toggleExternalLinkPopup}
          toggleModal={handleExternalLinkPopup}
          title={externalLinkModalTitle}
          message={externalLinkModalBody}
        />
      )}
    </Container>
  )
}

const getWhiteLabels = (whiteLabel: RootStateTypeExtra['whiteLabel']) => {
  const { remote } = mapWhiteLabelsWithDefaults(whiteLabel)
  const {
    backButtonTitle: buildingBackButtonTitle,
    panoramicButtonTitle: propertyViewPanoramicButtonTitle,
    externalLinkModalTitle,
    externalLinkModalBody,
  } = remote.propertyView

  const { panoramicButtonTitle: floorViewPanoramicButtonTitle } =
    remote.floorStageView

  return {
    buildingBackButtonTitle,
    propertyViewPanoramicButtonTitle,
    floorViewPanoramicButtonTitle,
    externalLinkModalTitle,
    externalLinkModalBody,
  }
}

export default connect(
  ({
    projectIdentity,
    firestore: { session },
    blockLevel,
    whiteLabel,
  }: RootStateTypeExtra) => ({
    session,
    projectIdentity,
    blockLevel,
    ...getWhiteLabels(whiteLabel),
  })
)(Building)
